import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";
import { SCHOOLS, CLINIC_PIN } from "@/config/content";
import { Reveal, SectionHeading } from "@/components/site/ui";

const pinIcon = (color) =>
  L.divIcon({
    className: "ss-pin",
    html: `<svg width="28" height="38" viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.3 0 0 6.3 0 14c0 9.9 14 24 14 24s14-14.1 14-24C28 6.3 21.7 0 14 0z" fill="${color}"/><circle cx="14" cy="14" r="5.5" fill="#fff"/></svg>`,
    iconSize: [28, 38],
    iconAnchor: [14, 38],
    popupAnchor: [0, -32],
  });

export const SchoolMap = () => {
  const elRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      maxZoom: 19,
    }).addTo(map);

    const bounds = [];
    SCHOOLS.forEach((s) => {
      L.marker([s.lat, s.lng], { icon: pinIcon("#0D9488") })
        .addTo(map)
        .bindPopup(`<strong style="font-family:Manrope,sans-serif;color:#0A192F">${s.name}</strong>`);
      bounds.push([s.lat, s.lng]);
    });

    if (CLINIC_PIN) {
      L.marker([CLINIC_PIN.lat, CLINIC_PIN.lng], { icon: pinIcon("#F59E0B") })
        .addTo(map)
        .bindPopup(`<strong style="font-family:Manrope,sans-serif;color:#0A192F">${CLINIC_PIN.name}</strong>`);
      bounds.push([CLINIC_PIN.lat, CLINIC_PIN.lng]);
    }

    if (bounds.length) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    else map.setView([-33.9, 150.9], 10);

    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; };
  }, []);

  return (
    <section id="school-map" className="py-20 lg:py-28 bg-white">
      <div className="max-site container-px">
        <SectionHeading
          center
          overline="Where We Work"
          title="Suburbs we've reached across New South Wales"
          intro="Straight Sight brings free, on-site vision programs directly to schools and childcare centres. Each pin marks a suburb where we've supported students and families."
        />
        <Reveal>
          <div className="mt-12 relative rounded-[2rem] overflow-hidden soft-shadow ring-1 ring-brand-navy/5">
            <div ref={elRef} className="h-[380px] sm:h-[460px] w-full" data-testid="schools-map" aria-label="Map of schools Straight Sight has visited" />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-6 text-sm text-brand-ink/75">
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-teal" /> Suburbs reached</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-amber" /> Straight Sight clinic</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
